/**
 * Staff API composable for federated staff authentication endpoints.
 */

import axios, { type AxiosResponse } from 'axios'
import type { SessionCheck, StaffSession } from '../stores/types/staff.types'
import { resolveApiBaseUrl } from '../services/apiBase'

interface ApiErrorPayload {
  errorCode?: string
  code?: string
}

interface ApiResponse<T> {
  data?: T
  error?: ApiErrorPayload
}

const staffHttp = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
})

const unwrap = <T>(p: Promise<AxiosResponse<T>>): Promise<T> =>
  p.then((r) => r.data)

function isAxiosError(error: unknown): error is { response?: { status?: number; data?: ApiResponse<unknown> } } {
  return typeof error === 'object' && error !== null && 'response' in error
}

function extractApiErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) return null
  const payload = error.response?.data
  const code = payload?.error?.errorCode || payload?.error?.code
  return typeof code === 'string' && code.length > 0 ? code : null
}

export function useStaffApi() {
  async function getSession(): Promise<StaffSession | null> {
    try {
      const response = await unwrap<ApiResponse<StaffSession>>(
        staffHttp.get('/v1/admin/auth/me', { withCredentials: true })
      )
      return response.data ?? null
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 401) {
        return null
      }
      throw error
    }
  }

  async function exchangeToken(token: string): Promise<StaffSession | null> {
    try {
      const response = await unwrap<ApiResponse<StaffSession>>(
        staffHttp.post('/v1/admin/auth/token-exchange', { token }, { withCredentials: true })
      )
      return response.data ?? null
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 401) {
        const responseErrorCode = extractApiErrorCode(error)
        throw { code: responseErrorCode ?? 'TOKEN_EXPIRED', status: 401 }
      }
      throw error
    }
  }

  async function checkSession(): Promise<SessionCheck> {
    try {
      const response = await unwrap<ApiResponse<SessionCheck>>(
        staffHttp.get('/v1/admin/auth/check', { withCredentials: true })
      )
      return response.data ?? { valid: false, slug: null }
    } catch (_error: unknown) {
      return { valid: false, slug: null }
    }
  }

  async function logout(): Promise<void> {
    try {
      await staffHttp.post('/v1/admin/auth/logout', {}, { withCredentials: true })
    } catch (error: unknown) {
      // Best effort.
      console.warn('Staff logout API call failed:', error)
    }
  }

  return {
    getSession,
    exchangeToken,
    checkSession,
    logout,
  }
}
